// props to kent c dodds epic react

import type { Dispatch, SetStateAction } from "react"
import { useEffect, useRef, useState } from "react"

interface IUseLocalStorage<S> {
  key: string
  initialState: S | (() => S)
  serialize?: (data: S) => string
  deserialize?: (serialized: string) => S
}

const useLocalStorageState = <S>({
  key,
  initialState,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
}: IUseLocalStorage<S>): [S, Dispatch<SetStateAction<S>>, VoidFunction] => {
  const [state, setState] = useState(() => {
    const valueInLocalStorage = window.localStorage.getItem(key)
    if (valueInLocalStorage) return deserialize(valueInLocalStorage)

    return typeof initialState === `function`
      ? (initialState as CallableFunction)()
      : initialState
  })

  const prevKeyRef = useRef(key)
  const clearState = () => {
    window.localStorage.removeItem(prevKeyRef.current)
    setState(``)
  }

  useEffect(() => {
    const prevKey = prevKeyRef.current
    if (prevKey !== key) {
      window.localStorage.removeItem(prevKey)
    }
    prevKeyRef.current = key
    window.localStorage.setItem(key, serialize(state))
  }, [key, serialize, state])

  return [state, setState, clearState]
}

export default useLocalStorageState
